import { clone } from 'ramda';
import { getBrandFromHostname } from './brand';

export type ThemeType = typeof arnoldTheme;

const arnoldTheme = {
  // Historical styles
  colorPrimary: '#51bcda',
  colorInfo: '#17a2b8',
  colorSecondary: '#b2e1ef',
  colorTertiary: '#f3fafc',
  colorReminder: 'yellow',
  login: {
    backgroundColor: '#f4f3ef',
  },
  background: {
    primary: '#FFFFFF',
  },

  // DSL styles
  shadows: {
    elevationContentBlock: '0px 10px 14px rgba(0, 0, 0, 0.01)',
    elevationContentBlockHigh: '0px 2px 4px 0px rgba(0, 0, 0, 0.08)',
    elevationContentBlockOutstanding: '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
    elevationContentDialog: '0px 5px 30px -20px rgba(0, 0, 0, 0.5)',
  },
  colors: {
    brand: {
      primary: '#04B7EF',
      accent: '#009EDC',
    },
    ai: {
      default: '#9B37FF',
    },
    text: {
      primary: '#0D0A0B',
      secondary: '#767676',
      disabled: '#A1A1A1',
    },
    form: {
      error: '#af4c62',
    },
    textInverted: {
      primary: '#FFFFFF',
      secondary: '#BDBDBD',
      disabled: '#959595',
    },
    actionPrimary: {
      default: '#04b7ef',
      hover: '#04A5D8',
      active: '#0390BD',
      focused: '#03C3FF',
      disabled: '#A1A1A1',
    },
    actionSecondary: {
      default: '#CCCCCC',
      hover: '#04A5D8',
      active: '#0390BD',
      focused: '#03C3FF',
      disabled: '#A1A1A1',
    },
    actionInvertedPrimary: {
      default: '#F0F0F0',
      hover: '#E8E5E5',
      active: '#CCCCCC',
      focused: '#F7F7F7',
      disabled: '#F8F8F8',
    },
    actionInvertedSecondary: {
      default: '#026482',
      hover: '#01465A',
      active: '#013342',
      focused: '#038BB5',
      disabled: '#B1DAE7',
    },
    backgroundBasic: {
      default: '#F9F9F9',
      hover: '#F5F5F5',
      active: '#F0F0F0',
      disabled: '#E7E7E7',
    },
    backgroundCover: {
      default: '#FFFFFF',
      hover: '#F9F9F9',
      active: '#F9F9F9',
      disabled: '#FFFFFF',
    },
    backgroundDark: {
      default: '#3F3F3F',
    },
    borderMain: {
      default: '#D2D5D6',
      hover: '#04A5D8',
      active: '#0390BD',
      focused: '#03C3FF',
      disabled: '#E7E7E7',
    },
    borderSeparator: {
      default: '#EDEDED',
    },
    emotionNeutral: {
      default: '#0D0A0B',
      hover: '#0D0A0B',
      active: '#0D0A0B',
      disabled: '#F8F8F8',
      background: '#F2F4F5',
    },
    emotionInformative: {
      default: '#08A0C1',
      hover: '#05687D',
      active: '#06809A',
      disabled: '#F8F8F8',
      background: '#F3FAFC',
    },
    emotionSuccess: {
      default: '#23A917',
      hover: '#176E0F',
      active: '#1C8712',
      disabled: '#F8F8F8',
      background: '#F4FBF3',
      backgroundHover: '#D3EED1',
      backgroundActive: '#E9F6E8',
    },
    emotionWarning: {
      default: '#D0990D',
      hover: '#876308',
      active: '#A67A0A',
      disabled: '#F8F8F8',
      background: '#FDFAF3',
      backgroundHover: '#F6EBCF',
      backgroundActive: '#FAF5E7',
    },
    emotionDanger: {
      default: '#CF2630',
      hover: '#87191F',
      active: '#A61E26',
      disabled: '#F8F8F8',
      background: '#FDF4F5',
      backgroundHover: '#F5D4D6',
      backgroundActive: '#FAE9EA',
    },
    iconText: {
      primary: '#535353',
      secondary: '#9C9C9C',
      disabled: '#C1C1C1',
    },
    iconAction: {
      primary: '#04B7EF',
    },
    chart: {
      neutral: ['#30D0F2', '#20B3D3', '#1C9CB8', '#148CA6', '#077E98'],
      warning: ['#F9C475', '#F4B049', '#EC9E29', '#E28801'],
      alert: ['#E15E5E', '#CC454D', '#C22C2C', '#B00606'],
    },
  },
  spacing: {
    a: '1px',
    b: '2px',
    c: '4px',
    d: '8px',
    e: '12px',
    f: '16px',
    g: '24px',
    h: '32px',
    i: '40px',
    j: '48px',
    k: '56px',
    l: '64px',
    m: '80px',
  },
  borderRadius: {
    xs: '2px',
    s: '4px',
    m: '6px',
    l: '8px',
    xl: '12px',
    xxl: '15px',
  },
  typography: {
    display: {
      large: {
        default: {
          family: 'Quicksand',
          fontWeight: 700,
          fontSize: '64px',
          lineHeight: '120%',
        },
      },
    },
    heading: {
      xLarge: {
        default: {
          family: 'Quicksand',
          fontWeight: 700,
          fontSize: '48px',
          lineHeight: '120%',
        },
      },
      large: {
        default: {
          family: 'Roboto',
          fontWeight: 300,
          fontSize: '34px',
          lineHeight: '120%',
        },
        bold: {
          fontWeight: 500,
        },
      },
      medium: {
        default: {
          family: 'Roboto',
          fontWeight: 400,
          fontSize: '22px',
          lineHeight: '120%',
        },
        bold: {
          family: 'Roboto',
          fontWeight: 700,
          fontSize: '22px',
          lineHeight: '120%',
        },
      },
      small: {
        default: {
          family: 'Roboto',
          fontWeight: 500,
          fontSize: '17px',
          lineHeight: '120%',
        },
      },
    },
    body: {
      xLarge: {
        regular: {
          family: 'Roboto',
          fontWeight: 400,
          fontSize: '20px',
          lineHeight: '145%',
        },
        bold: {
          family: 'Roboto',
          fontWeight: 700,
          fontSize: '20px',
          lineHeight: '145%',
        },
        italic: {
          family: 'Roboto',
          fontWeight: 400,
          fontSize: '20px',
          lineHeight: '145%',
        },
        link: {
          family: 'Roboto',
          fontWeight: 500,
          fontSize: '20px',
          lineHeight: '145%',
          letterSpacing: '.05rem',
        },
        button: {
          family: 'Roboto',
          fontWeight: 500,
          fontSize: '20px',
          lineHeight: '145%',
          letterSpacing: '.05rem',
        },
      },
      large: {
        regular: {
          family: 'Roboto',
          fontWeight: 400,
          fontSize: '17px',
          lineHeight: '145%',
        },
        bold: {
          family: 'Roboto',
          fontWeight: 700,
          fontSize: '17px',
          lineHeight: '145%',
        },
        italic: {
          family: 'Roboto',
          fontWeight: 400,
          fontSize: '17px',
          lineHeight: '145%',
        },
        link: {
          family: 'Roboto',
          fontWeight: 500,
          fontSize: '17px',
          lineHeight: '145%',
          letterSpacing: '.05rem',
        },
        button: {
          family: 'Roboto',
          fontWeight: 500,
          fontSize: '17px',
          lineHeight: '145%',
          letterSpacing: '.05rem',
        },
      },
      medium: {
        regular: {
          family: 'Roboto',
          fontWeight: 400,
          fontSize: '15px',
          lineHeight: '145%',
        },
        bold: {
          family: 'Roboto',
          fontWeight: 700,
          fontSize: '15px',
          lineHeight: '145%',
        },
        italic: {
          family: 'Roboto',
          fontWeight: 400,
          fontSize: '15px',
          lineHeight: '145%',
        },
        link: {
          family: 'Roboto',
          fontWeight: 500,
          fontSize: '15px',
          lineHeight: '145%',
          letterSpacing: '.05rem',
        },
        button: {
          family: 'Roboto',
          fontWeight: 500,
          fontSize: '15px',
          lineHeight: '145%',
          letterSpacing: '.05rem',
        },
      },
      small: {
        regular: {
          family: 'Roboto',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '145%',
        },
        bold: {
          family: 'Roboto',
          fontWeight: 700,
          fontSize: '12px',
          lineHeight: '145%',
        },
        italic: {
          family: 'Roboto',
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '145%',
        },
        link: {
          family: 'Roboto',
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '145%',
          letterSpacing: '.05rem',
        },
        button: {
          family: 'Roboto',
          fontWeight: 500,
          fontSize: '12px',
          lineHeight: '145%',
          letterSpacing: '.05rem',
        },
      },
    },
  },
  breakpoint: {
    small: '768px',
    medium: '1024px',
    large: '1440px',
  },
};

// Deep-copy defaults so nested Pulser overrides cannot change Arnold.
const pulserTheme: ThemeType = clone(arnoldTheme);
pulserTheme.colors.brand.primary = '#0070BF';
pulserTheme.colors.actionPrimary.default = pulserTheme.colors.brand.primary;
pulserTheme.colors.iconAction.primary = pulserTheme.colors.brand.primary;
// Related shades, estimated to keep the Arnold relationship to the primary colour
// (Arnold #04B7EF -> bright #03C3FF, soft #51BCDA, info #17A2B8).
pulserTheme.colorPrimary = '#2C93D6';
pulserTheme.colorInfo = '#106A93';
pulserTheme.colors.actionPrimary.focused = '#007BD1';
pulserTheme.colors.actionSecondary.focused = pulserTheme.colors.actionPrimary.focused;
pulserTheme.colors.borderMain.focused = pulserTheme.colors.actionPrimary.focused;
pulserTheme.colors.emotionInformative.default = '#04659A'; // Arnold #08A0C1
pulserTheme.colors.brand.accent = '#005EAD'; // Arnold #009EDC, used by the onboarding/fallback category icons

const hostname = typeof window === 'undefined' ? '' : window.location.hostname;
const search = typeof window === 'undefined' ? '' : window.location.search;
export const brand = getBrandFromHostname(hostname, search);
export const theme = brand === 'pulser' ? pulserTheme : arnoldTheme;
